import React, { useState, useEffect, useContext } from 'react';
import { getApolloContext, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import { useLocation, useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import { QuestionCircleOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { usePosition } from 'use-position';
import { useFetch } from '../../../utils/useFetch';
import { sortShippingGroups, locationApi } from '../../../utils/sortShippingGroups';

import {
  MutationAddPackageArgs,
  MutationUpdatePackageArgs,
  MutationUpdateUserArgs,
  Package,
  PackageQueryResult,
  PackageStatus,
  QueryQueryUserPackagesArgs,
  ShippingGroup,
  ShippingGroupType,
  User,
} from '../../../utils/types';
import PageTitle from '../../../components/PageTitle';
import AppContext from '../../../components/AppContext';
import { GROUP_TYPE } from '../../../utils/constants';

const { Option, OptGroup } = Select;

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

const shippingGroupEnabled = true;
const ANIMATION_INTERVAL = 800;
const LAST_STEP_INTERVAL = 800;

enum AnimationState {
  UpdatePersonalInfo,
  QueryExistingPackage,
  FoundPackage,
  NoPackageFoundAddingPackage,
  Done,
  FoundPackageWithInfo,
  Editing,
  EditingDone,
}

const SHIPPING_GROUP_FIELDS = gql`
  {
    id
    name
    type
    zipCode
    country
    originalShippingGroupId
    latitude
    longitude
    isPrivate
    hasFixedLeader
    previousMembership {
      canBeLeader
      reason
    }
  }
`;

const SHIPPING_GROUP_QUERY = gql`
  query ShippingGroupQuery($status: ShippingGroupStatus) {
    shippingGroups(status: $status) ${SHIPPING_GROUP_FIELDS}
  }
`;

const ADD_PACKAGE_MUTATION = gql`
  mutation AddPackageMutation($package: AddPackageInput!, $updateMembership: MembershipUpdateInput) {
    addPackage(package: $package, updateMembership: $updateMembership) {
      id
      shippingCompany
      trackingNumber
      items
      notes
      status
      shippingGroup ${SHIPPING_GROUP_FIELDS}
    }
  }
`;

const UPDATE_PACKAGE_MUTATION = gql`
  mutation updatePackageMutation($package: PackageInput!, $id: ID!, $updateMembership: MembershipUpdateInput) {
    updatePackage(package: $package, id: $id, updateMembership: $updateMembership) {
      id
      shippingCompany
      trackingNumber
      items
      notes
      status
      shippingGroup ${SHIPPING_GROUP_FIELDS}
    }
  }
`;

const UPDATE_USER_MUTATION = gql`
  mutation updateUser($user: UserInput!, $id: ID!) {
    updateUser(user: $user, id: $id) {
      id
    }
  }
`;

export const PACKAGES_QUERY = gql`
  query PackagesQuery(
    $where: PackageWhereInput!
    $orderBy: PackageOrderByInput
    $skip: Int
    $count: Int
  ) {
    queryUserPackages(where: $where, orderBy: $orderBy, skip: $skip, count: $count) {
      total
      list {
        id
        trackingNumber
        shippingCompany
        items
        status
        notes
        warehouseNotes
        weight
        length
        width
        depth
        shippingGroup {
          id
          name
          type
          status
          isPrivate
        }
        order {
          id
          number
          status
          country
        }
        subOrder {
          id
          number
          status
        }
      }
    }
  }
`;

enum PackageType {
  Group = 'GROUP',
  Direct = 'DIRECT',
}

const LAST_FOOD_GROUP = 'last-food-group';
const LAST_REGULAR_GROUP = 'last-regular-group';

function useShippingGroups(options: {
  onCompleted: (shippingGroups: Array<ShippingGroup>) => void;
}): {
  loading: boolean;
  shippingGroups: Array<ShippingGroup>;
  shippingGroupsTable: { [key: string]: ShippingGroup };
  lastUsedGroups: { [K in ShippingGroupType]: ShippingGroup | undefined };
} {
  const position = usePosition();
  const ipPositionRequest = useFetch(locationApi);

  const { loading, data: groupsData = { shippingGroups: [] as Array<ShippingGroup> } } = useQuery(
    SHIPPING_GROUP_QUERY,
    {
      variables: { status: 'OPEN' },
      fetchPolicy: 'cache-and-network',
      skip: !shippingGroupEnabled,
      onCompleted: data => options.onCompleted(data.shippingGroups),
    },
  );

  const lastFoodGroupOriginalId = localStorage.getItem(LAST_FOOD_GROUP);
  const lastFoodGroup = lastFoodGroupOriginalId
    ? groupsData.shippingGroups.find(
        g =>
          g.originalShippingGroupId === lastFoodGroupOriginalId || g.id === lastFoodGroupOriginalId,
      )
    : undefined;
  const lastRegularGroupOriginalId = localStorage.getItem(LAST_REGULAR_GROUP);
  const lastRegularGroup = lastRegularGroupOriginalId
    ? groupsData.shippingGroups.find(
        g =>
          g.originalShippingGroupId === lastRegularGroupOriginalId ||
          g.id === lastRegularGroupOriginalId,
      )
    : undefined;

  const shippingGroups = sortShippingGroups(groupsData.shippingGroups, position, ipPositionRequest);

  const shippingGroupsTable = shippingGroups.reduce((acc, group) => {
    acc[group.id] = group;
    return acc;
  }, {});

  return {
    loading,
    shippingGroups,
    shippingGroupsTable,
    lastUsedGroups: {
      [ShippingGroupType.Regular]: lastRegularGroup,
      [ShippingGroupType.Food]: lastFoodGroup,
    },
  };
}

function saveLastUsedGroup(shippingGroup: ShippingGroup | null | undefined, code = '') {
  const shippingGroupId = shippingGroup?.originalShippingGroupId ?? shippingGroup?.id;
  if (shippingGroupId) {
    const key = shippingGroup?.type === 'FOOD' ? LAST_FOOD_GROUP : LAST_REGULAR_GROUP;
    localStorage.setItem(key, shippingGroupId);
    if (code && shippingGroup?.isPrivate) {
      localStorage.setItem(shippingGroupId, code);
    }
  }
}

type Mode = 'add' | 'edit';

function AddOrEditPackage(props: { mode: Mode }) {
  const { mode } = props;

  const history = useHistory();

  type FormValues = {
    trackingNumber: string,
    shippingCompany: string,
    shippingGroupId?: string | null,
    shippingGroupPrivateCode?: string | null,
    canBeLeader?: boolean | null,
    notes?: string | null,
    items?: string | null,
    reason?: string | null,
    recipientName?: string | null,
    phone?: string | null,
    address?: string | null,
    city?: string | null,
    state?: string | null,
    zipCode?: string | null,
  };

  // Initial data
  let initialPackageType: PackageType | undefined;
  let initialGroupType: ShippingGroupType | undefined;
  let initialFormData: Partial<FormValues>;
  let packageId: string | undefined;
  let initialOriginalShippingGroup: string | null | undefined;

  let restrictedEdit = false;

  const query = useURLQuery();
  const location = useLocation<{ package: Package }>();

  const { user, refetchUserInfo } = useContext(AppContext);

  const hasUserInfo = !!(user.recipientName && user.phone && user.address && user.city && user.state && user.zipCode);

  if (mode === 'add') {
    // For add package
    const shippingGroupId = query.get('groupId');
    initialPackageType = query.get('type') as PackageType;
    initialGroupType = query.get('groupType') as ShippingGroupType;
    initialFormData = {};
    initialOriginalShippingGroup = shippingGroupId;
  } else {
    // For edit package
    const pkg = location?.state?.package || {};
    const { shippingGroup, ...rest } = pkg;

    initialPackageType = shippingGroup ? PackageType.Group : PackageType.Direct;
    initialGroupType = shippingGroup ? shippingGroup.type : ShippingGroupType.Regular;
    initialFormData = { ...rest, shippingGroupId: pkg.shippingGroup?.id };
    initialOriginalShippingGroup = shippingGroup?.id;
    packageId = pkg.id;

    restrictedEdit = pkg.status !== 'WAREHOUSE_PENDING';
  }

  const [packageType, setPackageType] = useState<PackageType>(
    initialPackageType || PackageType.Group,
  );
  const [groupType, setGroupType] = useState<ShippingGroupType>(
    initialGroupType || ShippingGroupType.Regular,
  );

  const [form] = Form.useForm<FormValues>();

  // Handle loading data
  const {
    loading: loadingGroups,
    shippingGroups,
    shippingGroupsTable,
    lastUsedGroups,
  } = useShippingGroups({
    onCompleted: shippingGroups => {
      if (initialOriginalShippingGroup) {
        // Update shipping group id in initial form data
        const group = shippingGroups.find(
          g =>
            g.id === initialOriginalShippingGroup ||
            g.originalShippingGroupId === initialOriginalShippingGroup,
        );
        if (group) {
          form.setFieldsValue({ shippingGroupId: group.id });
        }
      }
    },
  });
  const lastUsedGroup = lastUsedGroups[groupType];

  const { client: apolloClient } = React.useContext(getApolloContext());

  const [animationStages, setAnimationStages] = useState<Array<AnimationState>>([]);
  const [animationStartTime, setAnimationStartTime] = useState<number | null>(null);
  const [animationCompleteCallback, setAnimationCompleteCallback] = useState<() => void>(
    () => {},
  );

  function updateAnimationStages(newStages: Array<AnimationState>) {
    const prevStageCount = animationStages.length;
    const newStageCount = newStages.length;
    const now = new Date().getTime();
    if (newStageCount === 0) {
      setAnimationStartTime(null);
    } else if (animationStartTime === null || prevStageCount === 0) {
      setAnimationStartTime(now);
    } else {
      const originalEndTime = animationStartTime + newStageCount * ANIMATION_INTERVAL;
      const newEndTime = now + (newStageCount - prevStageCount) * ANIMATION_INTERVAL;
      const startTime = Math.max(originalEndTime, newEndTime) - newStageCount * ANIMATION_INTERVAL;
      setAnimationStartTime(startTime);
    }
    setAnimationStages(newStages);
  }

  const packagesQuery = apolloClient?.query<
    {
      queryUserPackages: PackageQueryResult;
    },
    QueryQueryUserPackagesArgs
  >({
    query: PACKAGES_QUERY,
    variables: {
      where: {
        status: { in: [PackageStatus.WarehousePending, PackageStatus.WarehouseReceived] },
      },
    },
    fetchPolicy: 'no-cache', // apollo client doesnt support cache-and-network
  });

  const [addPackage] = useMutation<{ addPackage: Package }, MutationAddPackageArgs>(
    ADD_PACKAGE_MUTATION,
    {
      onError: () => updateAnimationStages([]),
      onCompleted: data => {
        saveLastUsedGroup(
          data?.addPackage?.shippingGroup,
          form.getFieldValue('shippingGroupPrivateCode'),
        );
      },
    },
  );

  const [updatePackage] = useMutation<{ updatePackage: Package }, MutationUpdatePackageArgs>(
    UPDATE_PACKAGE_MUTATION,
    {
      onError: () => updateAnimationStages([]),
      onCompleted: data => {
        saveLastUsedGroup(
          data?.updatePackage?.shippingGroup,
          form.getFieldValue('shippingGroupPrivateCode'),
        );
      },
    },
  );

  const [updateUser] = useMutation<{ updateUser: User }, MutationUpdateUserArgs>(
    UPDATE_USER_MUTATION,
  );

  const onFinish = async (values: FormValues) => {
    const animationStages: AnimationState[] = [];

    function addAnimationStage(state: AnimationState) {
      animationStages.push(state);
      updateAnimationStages(animationStages);
    }

    const shippingGroup = values.shippingGroupId ? findShippingGroup(values.shippingGroupId) : null;

    const membership = packageType === PackageType.Group && shippingGroup && !shippingGroup.hasFixedLeader && values.canBeLeader !== undefined && values.canBeLeader !== null ? {
      canBeLeader: values.canBeLeader,
      reason: values.reason,
    } : null;

    if (membership?.canBeLeader && shippingGroup?.country && values.address) {
      // Need to update address
      addAnimationStage(AnimationState.UpdatePersonalInfo);
      await updateUser({
        variables: {
          user: {
            recipientName: values.recipientName,
            phone: values.phone,
            address: values.address,
            city: values.city,
            state: values.state,
            zipCode: values.zipCode,
            country: shippingGroup.country,
          },
          id: user.id,
        }
      });
      await refetchUserInfo();
    }

    type NextRoute = {
      pathname: string,
      state?: {
        package: Package,
      },
    };
    let nextRoute: NextRoute | null = null;

    if (mode === 'add') {
      addAnimationStage(AnimationState.QueryExistingPackage);
      const trackingNumber = values.trackingNumber.replace(/\s+/g, '').toUpperCase();
      values.trackingNumber = trackingNumber;
      const packagesResult = await packagesQuery;
      const match = packagesResult?.data?.queryUserPackages.list.filter(
        pkg => pkg.trackingNumber === trackingNumber,
      );

      if (match && match.length > 0) {
        // Found matching package
        const pkg = match[0];
        console.log('Found matching package:', pkg);
        if (pkg.notes || pkg.items || pkg.shippingGroup) {
          // Matching package contains data, jump to edit page
          console.log('Package contains info, switch to edit package page instead');

          addAnimationStage(AnimationState.FoundPackageWithInfo);
          nextRoute = {
            pathname: `/packages/${pkg.id}/edit`,
            state: {
              package: pkg,
            },
          };
        } else {
          // Matching package does not contain data, update package instead
          console.log('Fresh package, update data');
          addAnimationStage(AnimationState.FoundPackage);
          const updateResult = await updatePackage({
            variables: {
              package: {
                notes: values.notes,
                items: values.items,
                shippingGroupId:
                  packageType === PackageType.Direct ? undefined : values.shippingGroupId,
                shippingGroupPrivateCode: values.shippingGroupPrivateCode,
              },
              id: pkg.id,
            },
          });

          if (updateResult && !updateResult.errors) {
            addAnimationStage(AnimationState.Done);
            nextRoute = {
              pathname: updateResult.data?.updatePackage.status === PackageStatus.WarehousePending
                ? '/packages?tab=WAREHOUSE_PENDING'
                : '/packages?tab=WAREHOUSE_RECEIVED',
            };
          }
        }
      } else {
        // No matching package
        console.log('No matching package.');

        addAnimationStage(AnimationState.NoPackageFoundAddingPackage);

        const packageData = {
          trackingNumber: values.trackingNumber,
          shippingCompany: values.shippingCompany,
          ...(packageType === PackageType.Direct ? {} : {
            shippingGroupId: values.shippingGroupId,
            shippingGroupPrivateCode: values.shippingGroupPrivateCode,
          }),
          notes: values.notes,
          items: values.items,
        };

        const addResult = await addPackage({ variables: { package: packageData, updateMembership: membership } });
        
        if (addResult && !addResult.errors) {
          addAnimationStage(AnimationState.Done);
          nextRoute = {
            pathname: '/packages?tab=WAREHOUSE_PENDING',
          };
        }
      }
    } else {
      // Edit package
      addAnimationStage(AnimationState.Editing);

      const packageData = {
        ...(restrictedEdit ? {} : {
          trackingNumber: values.trackingNumber,
          shippingCompany: values.shippingCompany,
        }),
        ...(packageType === PackageType.Direct && initialFormData.shippingGroupId ? {
          // Set empty when package changes from group to direct
          shippingGroupId: '',
        } : {
          shippingGroupId: values.shippingGroupId,
          shippingGroupPrivateCode: values.shippingGroupPrivateCode,
        }),
        notes: values.notes,
        items: values.items,
      };

      const updateResult = await updatePackage({ variables: { package: packageData, id: packageId || '', updateMembership: membership } });

      if (updateResult && !updateResult.errors) {
        addAnimationStage(AnimationState.EditingDone);
        nextRoute = {
          pathname: updateResult.data?.updatePackage.status === PackageStatus.WarehousePending
            ? '/packages?tab=WAREHOUSE_PENDING'
            : '/packages?tab=WAREHOUSE_RECEIVED',
        };
      }
    }

    setAnimationCompleteCallback(() => () => {
      if (nextRoute) {
        if (nextRoute.state) {
          history.replace(nextRoute);
        } else {
          history.replace(nextRoute.pathname);
        }
      }
    });
  };

  const autoSelectShippingCompany = e => {
    const { value } = e.target;
    if (value && value.length >= 2) {
      switch (value.substring(0, 2).toUpperCase()) {
        case 'SF':
          form.setFieldsValue({ shippingCompany: '顺丰速运' });
          break;
        case 'YT':
          form.setFieldsValue({ shippingCompany: '圆通速递' });
          break;
        case 'TT':
          form.setFieldsValue({ shippingCompany: '天天快递' });
          break;
        case 'JD':
          form.setFieldsValue({ shippingCompany: '京东快递' });
          break;
        case 'ZJ':
          form.setFieldsValue({ shippingCompany: '宅急送' });
          break;
        case 'DP':
          form.setFieldsValue({ shippingCompany: '德邦快递' });
          break;
      }
    }
    return;
  };

  function getSelectedShippingGroup(getFieldValue): ShippingGroup | null | undefined {
    const shippingGroupId = getFieldValue('shippingGroupId');
    return shippingGroupsTable[shippingGroupId];
  }

  function findShippingGroup(id: string | null | undefined): ShippingGroup | null | undefined {
    return id ? shippingGroupsTable[id] : null;
  }

  return (
    <div className="AddPackage">
      <PageTitle>{mode === 'add' ? '预报包裹' : '修改包裹'}</PageTitle>
      <Card bordered={false}>
        <Alert
          type="warning"
          message="若物流显示包裹已签收，就可能已经入库了(默认类型直邮，可修改)，请先检查我的包裹"
        />
        {restrictedEdit && <Alert type="warning" message="若包裹已入库，则无法修改快递信息" />}
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={initialFormData}>
          {shippingGroupEnabled && (
            <Form.Item label="类型" rules={[{ required: true, message: '必填' }]}>
              <Radio.Group onChange={e => setPackageType(e.target.value)} value={packageType}>
                <Radio value={PackageType.Direct}>
                  直邮&nbsp;
                  <Tooltip title="直邮适用于两种情况：1. 后续想把多个包裹合并直邮到自己家 2. 暂不选拼邮团，等包裹都到齐了再修改加入拼邮团">
                    <QuestionCircleOutlined />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </Tooltip>
                </Radio>
                <Radio value={PackageType.Group}>拼邮&nbsp;&nbsp;&nbsp;&nbsp;</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {shippingGroupEnabled && packageType === PackageType.Group && (
            <Form.Item label="选择拼邮团（私人团需要参团口令）" required>
              <Input.Group compact>
                <Form.Item noStyle>
                  <Select
                    placeholder=""
                    style={{ width: '75px' }}
                    value={groupType}
                    onChange={value => {
                      setGroupType(value);
                      form.setFieldsValue({ shippingGroupId: '' });
                    }}
                  >
                    {Object.entries(GROUP_TYPE).map(([type, value]) =>
                      <Option key={type} value={type}>
                        {value.short}
                      </Option>
                    )}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="shippingGroupId"
                  rules={[{ required: true, message: '必填' }]}
                  style={{ width: 'calc(100% - 75px)', marginBottom: '0' }}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    loading={loadingGroups}
                    placeholder=""
                    style={{ width: '100%' }}
                    onChange={value => {
                      const shippingGroup = findShippingGroup(value?.toString());
                      form.setFieldsValue({
                        canBeLeader: shippingGroup?.previousMembership?.canBeLeader ?? true,
                        reason: shippingGroup?.previousMembership?.reason,
                      });
                    }}
                  >
                    {lastUsedGroup && (
                      <OptGroup label="最近参加的团">
                        <Option key={lastUsedGroup.id} value={lastUsedGroup.id}>
                          {lastUsedGroup.isPrivate ? '🔒 ' : ''}
                          {lastUsedGroup.country} {lastUsedGroup.zipCode} {lastUsedGroup.name}
                        </Option>
                      </OptGroup>
                    )}

                    <OptGroup label="附近拼邮团">
                      {shippingGroups
                        .filter(group => group.type === groupType && group.id !== lastUsedGroup?.id)
                        .map(group => (
                          <Option key={group.id} value={group.id}>
                            {group.isPrivate ? '🔒 ' : ''}
                            {group.country} {group.zipCode} {group.name}
                          </Option>
                        ))}
                    </OptGroup>
                  </Select>
                </Form.Item>
              </Input.Group>
            </Form.Item>
          )}

          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const selectedShippingGroup = getSelectedShippingGroup(getFieldValue);
              let savedPrivateCode = '';
              if (selectedShippingGroup?.isPrivate) {
                savedPrivateCode =
                  localStorage.getItem(selectedShippingGroup?.originalShippingGroupId || '') ||
                  localStorage.getItem(selectedShippingGroup?.id) ||
                  '';
              }
              return packageType === PackageType.Group &&
                selectedShippingGroup?.isPrivate &&
                (mode === 'add' || selectedShippingGroup.id !== initialOriginalShippingGroup) ? (
                <Form.Item
                  label="参团口令"
                  name="shippingGroupPrivateCode"
                  rules={[{ required: true, message: '必填' }]}
                  initialValue={savedPrivateCode}
                >
                  <Input allowClear />
                </Form.Item>
              ) : (
                <></>
              );
            }}
          </Form.Item>

          <Form.Item
            label={
              <span>
                国内快递单号（当当网也一样）
                <Tooltip title="淘宝下单的当当网的包裹可能看不到运单号，这种情况下需要跟淘宝客服要">
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
            name="trackingNumber"
            rules={[
              { required: true, message: '必填' },
              { pattern: /^[a-zA-Z0-9\-\s/]+$/, message: '单号格式有误，请勿包含中文等字符' },
            ]}
          >
            <Input allowClear onChange={autoSelectShippingCompany} disabled={restrictedEdit} />
          </Form.Item>

          <Form.Item
            name="shippingCompany"
            label="国内快递公司"
            rules={[{ required: true, message: '必填' }]}
          >
            <Select placeholder="" disabled={restrictedEdit}>
              <Option value="申通快递">申通快递</Option>
              <Option value="顺丰速运">顺丰速运</Option>
              <Option value="圆通速递">圆通速递</Option>
              <Option value="韵达速递">韵达速递</Option>
              <Option value="中通快递">中通快递</Option>
              <Option value="天天快递">天天快递</Option>
              <Option value="极兔速递">极兔速递</Option>
              <Option value="京东快递">京东快递</Option>
              <Option value="全峰快递">全峰快递</Option>
              <Option value="优速物流">优速物流</Option>
              <Option value="宅急送">宅急送</Option>
              <Option value="德邦快递">德邦快递</Option>
              <Option value="中铁物流">中铁物流</Option>
              <Option value="当当网">当当网</Option>
              <Option value="中国邮政EMS">中国邮政EMS</Option>
              <Option value="中国邮政包裹信件">中国邮政包裹信件</Option>
              <Option value="其他">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item label="物品描述" name="items" rules={[{}]}>
            <Input.TextArea allowClear />
          </Form.Item>

          <Form.Item label="备注" name="notes">
            <Input.TextArea allowClear />
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const selectedShippingGroup = getSelectedShippingGroup(getFieldValue);
              return packageType === PackageType.Group &&
                selectedShippingGroup?.hasFixedLeader === false ? (
                <>
                  <Form.Item
                    name="canBeLeader"
                    label="截团时，如果重量最重，是否愿意收货"
                    initialValue={selectedShippingGroup.previousMembership?.canBeLeader ?? true}
                    rules={[{ required: true, message: '必填' }]}
                  >
                    <Radio.Group>
                      <Radio value={true}>
                        愿意收货
                      </Radio>
                      <Radio value={false}>不愿意收货</Radio>
                    </Radio.Group>
                  </Form.Item>
                  { getFieldValue("canBeLeader") ?
                    (hasUserInfo ?
                      <div style={{ marginBottom: '16px' }}>
                        <Divider plain>收货地址</Divider>
                        <Alert type="warning" message="如果地址有误请到用户信息里修改。收件人和电话其他用户不可见，地址将在截团后对团员公开" style={{ marginBottom: '8px' }} />

                        <div>
                          <span style={{ fontWeight: 'bold' }}>{user.recipientName}</span>&nbsp;&nbsp;
                          {user.phone}
                        </div>
                        <div>
                          {user.address}, {user.city}, {user.state}, {user.country}&nbsp;{user.zipCode}
                        </div>
                      </div> :
                      <>
                        <Alert type="warning" message="请用目的国家的语言填写" style={{ marginBottom: '8px' }} />
                        <Form.Item
                          name="recipientName"
                          label="收件人姓名（对其他用户隐藏）"
                          rules={[{ required: true, message: '必填' }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name="phone"
                          label="收件人联系电话（对其他用户隐藏）"
                          rules={[{ required: true, message: '必填' }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name="address"
                          label="收货街道地址（截团后其他团员可见）"
                          rules={[{ required: true, message: '必填' }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name="city"
                          label="城市"
                          rules={[{ required: true, message: '必填' }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name="state"
                          label="州/省"
                          rules={[{ required: true, message: '必填' }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name="zipCode"
                          label="邮编（如果预计成为团长将会对团员公开）"
                          rules={[{ required: true, message: '必填' }]}
                        >
                          <Input />
                        </Form.Item>
                      </>
                    ) :
                    <>
                      <Form.Item
                        name="reason"
                        label="原因"
                        initialValue={selectedShippingGroup.previousMembership?.reason}
                        rules={[{ required: true, message: '必填' }]}
                      >
                        <Input placeholder="如果大家都不愿意收货，默认还是最重的人收"/>
                      </Form.Item>
                    </>
                  }
                </>
              ) : (
                <></>
              );
            }}
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit">
              {mode === 'add' ? '确认添加' : '确认修改'}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button block type="link" onClick={() => history.goBack()}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Card>
      <AnimationModal
        animationStages={animationStages}
        animationStartTime={animationStartTime}
        animationCompleteCallback={animationCompleteCallback}
      />
    </div>
  );
}

function AnimationModal(props: {
  animationStages: Array<AnimationState>;
  animationCompleteCallback: () => void;
  animationStartTime: number | null;
}) {
  // eslint-disable-next-line @typescript-eslint/ban-types
  const [, updateState] = useState<{}>();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const [callbackCalled, setCallbackCalled] = useState(false);

  const { animationStages, animationStartTime, animationCompleteCallback } = props;
  let index = animationStartTime
    ? Math.floor((new Date().getTime() - animationStartTime) / ANIMATION_INTERVAL)
    : null;

  if (index && index >= animationStages.length) {
    index = animationStages.length - 1;
  }
  const animationState =
    index !== null ? animationStages[index] : AnimationState.QueryExistingPackage;

  useEffect(() => {
    if (animationStages.length === 0) {
      setCallbackCalled(false);
    } else if (
      (animationState === AnimationState.Done ||
        animationState === AnimationState.EditingDone ||
        animationState === AnimationState.FoundPackageWithInfo) &&
      !callbackCalled
    ) {
      setCallbackCalled(true);
      setTimeout(animationCompleteCallback, ANIMATION_INTERVAL);
    } else if (animationStartTime) {
      setTimeout(() => forceUpdate(), LAST_STEP_INTERVAL);
    }
  });

  return (
    <Modal
      visible={animationStartTime != null}
      title={null}
      footer={null}
      closable={false}
      centered
      maskClosable={false}
      width={280}
    >
      {animationState === AnimationState.UpdatePersonalInfo && (
        <Space>
          <Spin />
          <span style={{ fontSize: '1.05rem' }}>正在更新个人信息...</span>
        </Space>
      )}
      {animationState === AnimationState.QueryExistingPackage && (
        <Space>
          <Spin />
          <span style={{ fontSize: '1.05rem' }}>正在检索仓库中包裹...</span>
        </Space>
      )}
      {animationState === AnimationState.FoundPackage && (
        <Space>
          <Spin />
          <span style={{ fontSize: '1.05rem' }}>检测到该包裹已入库，正在更新包裹信息...</span>
        </Space>
      )}
      {animationState === AnimationState.NoPackageFoundAddingPackage && (
        <Space>
          <Spin />
          <span style={{ fontSize: '1.05rem' }}>正在添加包裹...</span>
        </Space>
      )}
      {animationState === AnimationState.Done && (
        <Space>
          {/* <Typography.Text type="success"> */}
          <CheckCircleOutlined style={{ fontSize: '1.2rem', color: '#52c41a' }} />
          {/* </Typography.Text> */}
          <span style={{ fontSize: '1.05rem' }}>添加成功！</span>
        </Space>
      )}
      {animationState === AnimationState.FoundPackageWithInfo && (
        <Space>
          <Typography.Text type="warning">
            <WarningOutlined style={{ fontSize: '1.2rem' }} />
          </Typography.Text>
          <span style={{ fontSize: '1.05rem' }}>该包裹已经预报过，正跳转到修改包裹页面...</span>
        </Space>
      )}
      {animationState === AnimationState.Editing && (
        <Space>
          <Spin />
          <span style={{ fontSize: '1.05rem' }}>正在修改包裹...</span>
        </Space>
      )}
      {animationState === AnimationState.EditingDone && (
        <Space>
          {/* <Typography.Text type="success"> */}
          <CheckCircleOutlined style={{ fontSize: '1.2rem', color: '#52c41a' }} />
          {/* </Typography.Text> */}
          <span style={{ fontSize: '1.05rem' }}>修改成功！</span>
        </Space>
      )}
    </Modal>
  );
}

export default AddOrEditPackage;
